import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Instagram, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";
import { submitSemaforoLead } from "@/lib/api";
import { Kicker, Btn, Card, COLORI, AMBER, MUTED, LINE, NAVY } from "./SemaforoUi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const IG_URL = "https://www.instagram.com/massimilianopulla/";

const RIGA = {
  rosso: "Basta una bolletta fuori misura e si va sotto. Non è colpa vostra: è il conto che non torna. Ma da qui si risale.",
  giallo: "Abbastanza per non andare sotto, troppo poco per dormire tranquilli. E con questi stipendi, da solo, l'ossigeno non cresce.",
  verde: "Ma il verde di oggi non è garantito: ogni anno che i costi salgono e gli stipendi no, quei mesi si accorciano. Va difeso.",
};
const DENTRO = [
  "Le 3 cose che vi stanno togliendo ossigeno, con le vostre cifre",
  "Le 3 mosse dei primi 90 giorni: cosa fare, in che ordine",
  "Perché non è colpa vostra: il confronto con i vostri genitori",
];

export function Result({ res, onUnlock, onRestart }) {
  const col = COLORI[res.colore];
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} data-testid="semaforo-result">
      <Kicker color={col}>Il vostro semaforo</Kicker>
      <div className="rounded-2xl p-6 sm:p-8" style={{ background: "#0F1A30", border: `2px solid ${col}` }} data-testid="semaforo-result-card">
        <div className="flex items-center gap-3">
          <span className="h-4 w-4 rounded-full" style={{ background: col, boxShadow: `0 0 18px ${col}` }} />
          <span className="text-sm font-bold uppercase tracking-[0.25em]" style={{ color: col }} data-testid="semaforo-colore">Siete in {res.colore_label}</span>
        </div>
        <p className="mt-4 font-display font-black text-4xl sm:text-5xl leading-[1.02] tracking-tight" data-testid="semaforo-mesi">Reggete {res.mesi_label}.</p>
        <p className="mt-2 text-sm sm:text-base" style={{ color: MUTED }}>se le entrate si fermano domani. Questo è il vostro ossigeno.</p>
        <p className="mt-4 pt-4 text-sm sm:text-base leading-relaxed" style={{ borderTop: `1px solid ${LINE}` }} data-testid="semaforo-resto">{RIGA[res.colore]}</p>
      </div>

      <div className="mt-6 rounded-2xl p-6" style={{ background: "#0F1A30", border: `1px solid ${AMBER}` }} data-testid="semaforo-gate">
        <div className="flex items-center gap-2 mb-2"><Lock size={15} style={{ color: AMBER }} /><span className="text-xs uppercase tracking-[0.2em]" style={{ color: AMBER }}>Il Piano Ossigeno · {res.colore_label}</span></div>
        <p className="font-display font-bold text-2xl leading-tight">Come si passa da {res.mesi_label} a qualcosa di più.</p>
        <ul className="mt-4 space-y-2.5">
          {DENTRO.map((t, i) => (
            <li key={i} className="flex items-start gap-3 text-sm sm:text-base leading-snug" data-testid={`semaforo-dentro-${i + 1}`}>
              <span className="mt-1.5 h-2 w-2 rounded-full shrink-0" style={{ background: col }} />{t}
            </li>
          ))}
        </ul>
        <p className="mt-4 text-xs sm:text-sm" style={{ color: MUTED }}>In PDF, con le vostre cifre già calcolate. Gratis: lo sbloccate con nome ed email.</p>
        <div className="mt-5"><Btn onClick={onUnlock} testid="semaforo-unlock"><Download size={18} /> Sblocca il Piano Ossigeno</Btn></div>
      </div>

      <div className="mt-6 rounded-2xl p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4" style={{ background: "#0F1A30", border: `1px solid ${LINE}` }} data-testid="semaforo-follow-card">
        <div className="min-w-[220px] flex-1">
          <p className="font-display font-bold text-lg sm:text-xl leading-tight">Questo conto, ogni giorno, su Instagram.</p>
          <p className="mt-1 text-sm leading-relaxed" style={{ color: MUTED }}>Figli, stipendi, tasse: un reel al giorno, numeri veri, senza vendere sogni.</p>
        </div>
        <a href={IG_URL} target="_blank" rel="noreferrer" data-testid="semaforo-follow-result"
          className="inline-flex items-center gap-2 font-bold px-5 py-3 rounded-full transition-colors" style={{ background: NAVY, border: `1px solid ${AMBER}`, color: AMBER }}>
          <Instagram size={18} /> Segui @massimilianopulla
        </a>
      </div>
      <div className="mt-6 mb-20">
        <button onClick={onRestart} className="inline-flex items-center gap-1.5 text-sm" style={{ color: MUTED }} data-testid="semaforo-restart">
          <RotateCcw size={14} /> Rifai il test
        </button>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 px-4 py-3" style={{ background: "rgba(11,17,32,0.92)", borderTop: `1px solid ${LINE}`, backdropFilter: "blur(10px)" }} data-testid="semaforo-sticky-cta">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <p className="text-xs sm:text-sm leading-tight" style={{ color: MUTED }}><span className="text-white font-semibold">Piano Ossigeno</span> in PDF · gratis</p>
          <Btn onClick={onUnlock} testid="semaforo-unlock-sticky" className="!w-auto !py-3 !px-5 !text-sm sm:!text-base"><Download size={16} /> Sblocca il Piano</Btn>
        </div>
      </div>
    </motion.div>
  );
}

const inputCls = "w-full bg-[#0B1120] border border-[#3A4763] rounded-md px-4 py-3 text-white placeholder:text-[#475569] outline-none focus:border-[#F59E0B] transition-colors";

export function LeadForm({ res, testId, fonte, onDone }) {
  const [form, setForm] = useState(() => {
    const q = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
    return { nome: q.get("nome") || "", email: q.get("email") || "", telefono: q.get("tel") || "", consenso: false };
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim()) return toast.error("Servono nome ed email.");
    if (!form.consenso) return toast.error("Serve il consenso al trattamento dei dati.");
    setLoading(true);
    try {
      const lead = await submitSemaforoLead({ test_id: testId, ...form, fonte });
      if (typeof window.fbq === "function") {
        window.fbq("track", "CompleteRegistration", { content_name: "Semaforo Ossigeno", status: res.colore });
        window.fbq("trackCustom", "SemaforoLead", { colore: res.colore });
      }
      onDone(lead);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Qualcosa non ha funzionato: riprova.");
    } finally { setLoading(false); }
  };
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="semaforo-form-step">
      <Kicker>Il Piano Ossigeno · {res.colore_label}</Kicker>
      <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight">Dove ve lo mando?</h2>
      <p className="mt-3 text-sm sm:text-base leading-relaxed" style={{ color: MUTED }}>
        Le 3 mosse dei primi 90 giorni per la vostra famiglia ({res.situazione_label}, {res.mesi_label} di ossigeno). Lo scaricate subito e ve lo mando anche via email.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-3" data-testid="semaforo-lead-form">
        <input data-testid="semaforo-input-nome" type="text" value={form.nome} onChange={set("nome")} placeholder="Il tuo nome" autoComplete="name" className={inputCls} />
        <input data-testid="semaforo-input-email" type="email" value={form.email} onChange={set("email")} placeholder="La tua email migliore" autoComplete="email" inputMode="email" className={inputCls} />
        <input data-testid="semaforo-input-telefono" type="tel" value={form.telefono} onChange={set("telefono")} placeholder="Numero WhatsApp (facoltativo)" autoComplete="tel" inputMode="tel" className={inputCls} />
        <label className="flex items-start gap-3 text-xs leading-snug cursor-pointer" style={{ color: MUTED }}>
          <input data-testid="semaforo-input-consenso" type="checkbox" checked={form.consenso} onChange={set("consenso")} className="mt-0.5 w-4 h-4 accent-[#F59E0B] shrink-0" />
          <span>Acconsento al trattamento dei miei dati per ricevere il Piano Ossigeno e i contenuti di Massimiliano Pulla, come da{" "}
            <a href={`${BACKEND_URL}/api/privacy`} target="_blank" rel="noreferrer" className="underline" style={{ color: AMBER }}>informativa privacy</a>.</span>
        </label>
        <Btn type="submit" disabled={loading} testid="semaforo-form-submit">{loading ? "Un attimo…" : "Mandami il Piano Ossigeno"}</Btn>
        <p className="text-center text-xs" style={{ color: "#64748B" }}>Niente spam. Solo cose che servono davvero.</p>
      </form>
    </motion.div>
  );
}

export function Done({ lead, res }) {
  const col = COLORI[res.colore];
  const nome = (lead.nome || "").split(" ")[0];
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} data-testid="semaforo-done">
      <Kicker color={col}>Fatto, {nome}.</Kicker>
      <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight">Il vostro Piano Ossigeno è pronto.</h2>
      <p className="mt-3 text-sm sm:text-base leading-relaxed" style={{ color: MUTED }}>Dentro: dove siete, le 3 mosse, i tre conti con le vostre cifre, la regola che vale più di tutte. Ve lo mando anche via email.</p>
      <a href={lead.pdf_url} target="_blank" rel="noreferrer" data-testid="semaforo-pdf-link"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#F59E0B] hover:bg-[#d98a08] text-[#0B1120] font-bold text-base sm:text-lg px-6 py-4 rounded-full transition-colors">
        <Download size={18} /> Scarica il Piano Ossigeno (PDF)
      </a>

      <div className="mt-6 rounded-2xl p-6" style={{ background: "#0F1A30", border: `1px solid ${AMBER}` }} data-testid="semaforo-follow-card-done">
        <p className="font-display font-bold text-xl leading-tight">Ogni giorno, su Instagram, un pezzo di questo conto.</p>
        <p className="mt-2 text-sm sm:text-base leading-relaxed" style={{ color: MUTED }}>Figli, stipendi, tasse, pensioni: un reel al giorno, numeri veri, senza vendere sogni. Il Piano è il primo; il resto lo trovate lì.</p>
        <a href={IG_URL} target="_blank" rel="noreferrer" data-testid="semaforo-follow"
          className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 font-bold px-6 py-3.5 rounded-full transition-colors" style={{ background: AMBER, color: NAVY }}>
          <Instagram size={18} /> Segui @massimilianopulla
        </a>
      </div>

      <p className="mt-8 text-base sm:text-lg leading-relaxed" data-testid="semaforo-verdetto">{res.verdetto}</p>
      <div className="mt-6 grid gap-3">
        {res.minacce.map((m, i) => (
          <Card key={m.titolo} accent={LINE} testid={`semaforo-minaccia-${i + 1}`}>
            <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: col }}>{m.titolo}</p>
            <p className="mt-1.5 text-sm sm:text-base leading-relaxed">{m.testo}</p>
          </Card>
        ))}
      </div>
      <div className="mt-8">
        <Kicker>Perché non è colpa vostra</Kicker>
        <Card accent={AMBER} testid="semaforo-confronto"><p className="text-sm sm:text-base leading-relaxed">{res.confronto}</p></Card>
      </div>

    </motion.div>
  );
}
